import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useState, useEffect, useCallback, useMemo } from 'react'
import { useSnackbar } from 'notistack'
import { skipToken } from '@reduxjs/toolkit/query'
import dayjs from 'dayjs'

import {
    Box,
    Button,
    Chip,
    Container,
    FormControlLabel,
    Paper,
    Skeleton,
    Stack,
    Switch,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'
import GetAppIcon from '@mui/icons-material/GetApp'
import HourglassEmptyIcon from '@mui/icons-material/HourglassEmpty'
import PaidIcon from '@mui/icons-material/Paid'
import PaymentIcon from '@mui/icons-material/Payment'
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong'

import { BillingReviewStatus } from '@/core/models/consultation.model'

import WaitingRoomConsultationsTable from '../components/WaitingRoomConsultationsTable'
import ConsultationValidationModal from '../components/ConsultationValidationModal'
import ConsultationExportDialog, { ExportFilters } from '../components/ConsultationExportDialog'
import type { ConsultationValidationFormValues } from '../hooks/useConsultationValidationFormSchema'
import {
    useMarkConsultationsAsPaidMutation,
    useQueryWaitingRoomConsultationsQuery,
    useGetNetworkMembersQuery,
    useLazyExportWaitingRoomConsultationsQuery,
    useGetWaitingRoomConsultationsSummaryQuery,
} from '@/core/services/network'
import { useGetWaitingRoomQuery } from '@/core/services/waiting-room'
import FileSaver from 'file-saver'
import NotFoundPage from '@/pages/error/NotFoundPage'
import { useNetworkMemberPermissions } from '../hooks/useNetworkMemberPermissions'
import { formatPrice } from '@/utils/formatPrice'

export const limitOptions = [5, 10, 15, 25, 50]

// ─────────────────────────────────────────────────────────────────────────────
// Summary bar component
// ─────────────────────────────────────────────────────────────────────────────

function SummaryBar({ summary, isLoading }: {
    summary?: { total: number; paidCount: number; revenue: number; pendingCount: number; issueCount: number; completedCount: number }
    isLoading: boolean
}) {
    const { t } = useTranslation()

    if (isLoading || !summary) {
        return (
            <Paper variant="outlined" sx={{ p: 1.5 }}>
                <Stack direction="row" spacing={3} alignItems="center" flexWrap="wrap">
                    {[1, 2, 3, 4].map((i) => <Skeleton key={i} width={120} height={32} />)}
                </Stack>
            </Paper>
        )
    }

    return (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                <Chip
                    icon={<ReceiptLongIcon />}
                    label={t('page.consultation.summary.total', { defaultValue: '{{count}} consultations', count: summary.total })}
                    variant="outlined"
                />
                <Chip
                    icon={<PaidIcon />}
                    label={t('page.consultation.summary.paid', { defaultValue: '{{count}} payées — {{revenue}}', count: summary.paidCount, revenue: formatPrice(summary.revenue) })}
                    color="success"
                    variant="outlined"
                />
                <Chip
                    icon={<HourglassEmptyIcon />}
                    label={t('page.consultation.summary.pending', { defaultValue: '{{count}} en attente', count: summary.pendingCount })}
                    variant="outlined"
                />
                {summary.issueCount > 0 && (
                    <Chip
                        icon={<ErrorOutlineIcon />}
                        label={t('page.consultation.summary.issues', { defaultValue: '{{count}} problème(s)', count: summary.issueCount })}
                        color="error"
                        variant="outlined"
                    />
                )}
                <Chip
                    icon={<CheckCircleIcon />}
                    label={t('page.consultation.summary.completed', { defaultValue: '{{count}} terminées', count: summary.completedCount })}
                    color="success"
                    variant="filled"
                />
            </Stack>
        </Paper>
    )
}

// ─────────────────────────────────────────────────────────────────────────────
// Main container
// ─────────────────────────────────────────────────────────────────────────────

export default function WaitingRoomConsultations() {
    const { t } = useTranslation()
    const { enqueueSnackbar } = useSnackbar()
    const { networkId } = useParams<{ networkId: string }>()
    const { waitingRoomId } = useParams<{ waitingRoomId: string }>()
    const [limit, setLimit] = useState(limitOptions[0])
    const [page, setPage] = useState(1)
    const [showPaidOnly, setShowPaidOnly] = useState(false)
    const [billingReviewFilter, setBillingReviewFilter] = useState<'all' | BillingReviewStatus>('all')
    const [isValidationModalOpen, setIsValidationModalOpen] = useState(false)
    const [isExportModalOpen, setIsExportModalOpen] = useState(false)
    const [selectedConsultationIds, setSelectedConsultationIds] = useState<string[]>([])

    // Date range — default to current month
    const [dateFrom, setDateFrom] = useState(() => dayjs().startOf('month').format('YYYY-MM-DD'))
    const [dateTo, setDateTo] = useState(() => dayjs().endOf('month').format('YYYY-MM-DD'))

    const { isNetworkAdmin, hasDesmosConfigured } = useNetworkMemberPermissions({ networkId })
    const { data: waitingRoom } = useGetWaitingRoomQuery(waitingRoomId ?? skipToken)

    // Build date filters for the query
    const dateQuery = useMemo(() => {
        const q: Record<string, any> = {}
        if (dateFrom || dateTo) {
            q.startDate = {}
            if (dateFrom) q.startDate.$gte = new Date(dateFrom + 'T00:00:00').toISOString()
            if (dateTo) q.startDate.$lte = new Date(dateTo + 'T23:59:59').toISOString()
        }
        return q
    }, [dateFrom, dateTo])

    const { data: consultationsData, isLoading } = useQueryWaitingRoomConsultationsQuery(
        {
            networkId: networkId ?? '',
            waitingRoomId: waitingRoomId ?? '',
            body: {
                query: {
                    waitingRoom: waitingRoomId ?? '',
                    paid: showPaidOnly,
                    ...(billingReviewFilter !== 'all' ? { 'billingReview.status': billingReviewFilter } : {}),
                    ...dateQuery,
                },
                options: {
                    limit,
                    skip: (page - 1) * limit,
                    order: 'DESC',
                    orderBy: 'startDate',
                },
            },
        },
        {
            skip: !waitingRoomId || !isNetworkAdmin,
            refetchOnMountOrArgChange: true
        }
    )

    // Summary query
    const { data: summary, isLoading: isSummaryLoading } = useGetWaitingRoomConsultationsSummaryQuery(
        {
            networkId: networkId ?? '',
            waitingRoomId: waitingRoomId ?? '',
            ...(dateFrom ? { startDateFrom: new Date(dateFrom + 'T00:00:00').toISOString() } : {}),
            ...(dateTo ? { startDateTo: new Date(dateTo + 'T23:59:59').toISOString() } : {}),
        },
        { skip: !waitingRoomId || !isNetworkAdmin }
    )

    const { data: networkMembers = [] } = useGetNetworkMembersQuery(networkId ?? '', {
        skip: !networkId
    })

    const [markConsultationAsPaid, { isLoading: isValidating }] = useMarkConsultationsAsPaidMutation()
    const [triggerExport, { isLoading: isExporting }] = useLazyExportWaitingRoomConsultationsQuery()

    const [consultations, total] = consultationsData ?? [[], 0]

    // Handle case where current page becomes empty
    useEffect(() => {
        if (!isLoading && consultations.length === 0 && total > 0 && page > 1) {
            const maxPage = Math.ceil(total / limit)
            if (page > maxPage) {
                setPage(maxPage)
            }
        }
    }, [consultations.length, total, page, limit, isLoading])

    const handleChangePage = (newPage: number) => {
        const maxPage = Math.ceil(total / limit)
        setPage(Math.min(newPage, maxPage))
    }

    const handleChangeRowsPerPage = (newLimit: number) => {
        setLimit(newLimit)
        setPage(1)
    }

    const handleTogglePaidFilter = () => {
        setShowPaidOnly(!showPaidOnly)
        setPage(1)
        setSelectedConsultationIds([])
    }

    const handleBillingReviewFilterChange = (_: React.MouseEvent<HTMLElement>, value: 'all' | BillingReviewStatus | null) => {
        if (value !== null) {
            setBillingReviewFilter(value)
            setPage(1)
            setSelectedConsultationIds([])
        }
    }

    const resetSelection = useCallback(() => {
        setSelectedConsultationIds([])
        setIsValidationModalOpen(false)
    }, [])

    const handleValidateSelection = (selectedIds: string[]) => {
        if (selectedIds.length > 0) {
            setSelectedConsultationIds(selectedIds)
            setIsValidationModalOpen(true)
        } else {
            resetSelection()
        }
    }

    const handleCloseValidationModal = () => {
        if (!isValidating) {
            resetSelection()
        }
    }

    const handleValidationSubmit = async (values: ConsultationValidationFormValues) => {
        if (selectedConsultationIds.length === 0) return

        try {
            await markConsultationAsPaid({
                networkId: networkId ?? '',
                waitingRoomId: waitingRoomId ?? '',
                body: {
                    consultationIds: selectedConsultationIds,
                    price: values.price,
                    paymentType: values.paymentType,
                },
            }).unwrap()

            enqueueSnackbar(t('notification.consultation.validate.success'), { variant: 'success' })
            resetSelection()
        } catch (error) {
            console.error('Validation error:', error)
            enqueueSnackbar(t('notification.consultation.validate.error'), { variant: 'error' })
        }
    }

    const handleOpenExportModal = () => {
        setIsExportModalOpen(true)
    }

    const handleCloseExportModal = () => {
        setIsExportModalOpen(false)
    }

    const handleExport = async (filters: ExportFilters) => {
        if (!networkId || !waitingRoomId) return

        try {
            setIsExportModalOpen(false)
            enqueueSnackbar(t('page.consultation.export.preparing', { defaultValue: 'Préparation de l\'export...' }), { variant: 'info' })

            const response = await triggerExport({
                networkId,
                waitingRoomId,
                params: filters
            }).unwrap()

            if (!response.csvContent) {
                throw new Error(t('page.consultation.export.noData', { defaultValue: 'Aucune donnée disponible pour l\'export' }))
            }

            const blob = new Blob([response.csvContent], { type: 'text/csv;charset=utf-8' })
            FileSaver.saveAs(blob, response.fileName || 'consultations.csv')

            enqueueSnackbar(t('page.consultation.export.success', { defaultValue: 'Export réussi' }), { variant: 'success' })
        } catch (error) {
            console.error('Export error:', error)
            setIsExportModalOpen(false)
            const errorMessage = error instanceof Error
                ? error.message
                : t('page.consultation.export.error', { defaultValue: 'Échec de l\'export. Veuillez réessayer.' })
            enqueueSnackbar(errorMessage, { variant: 'error' })
        }
    }

    // Redirect if not an admin
    if (!isNetworkAdmin) {
        return (
            <Stack alignItems="center" justifyContent="center" width="100%" height="100%">
                <NotFoundPage />
            </Stack>
        )
    }

    return (
        <Container maxWidth="lg" sx={{ py: 4 }}>
            <Stack spacing={2}>
                {/* Header */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1 }}>
                    <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="h4">
                            {waitingRoom?.name ?? <Skeleton width={200} />}
                        </Typography>
                        {waitingRoom?.paymentDialogConfig?.enabled && (
                            <Chip
                                icon={<PaymentIcon />}
                                label={t('page.consultation.filter.paymentDialogEnabled', { defaultValue: 'Dialogue de paiement' })}
                                size="small"
                                color="info"
                                variant="outlined"
                            />
                        )}
                    </Stack>
                    <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
                        <Button
                            variant="outlined"
                            startIcon={<GetAppIcon />}
                            onClick={handleOpenExportModal}
                        >
                            {t('page.consultation.export.button', { defaultValue: 'Exporter CSV' })}
                        </Button>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={showPaidOnly}
                                    onChange={handleTogglePaidFilter}
                                    color="primary"
                                />
                            }
                            label={showPaidOnly
                                ? t('page.consultation.filter.showing_paid')
                                : t('page.consultation.filter.showing_unpaid')
                            }
                        />
                    </Stack>
                </Box>

                {/* Filters row */}
                <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap" useFlexGap>
                    {/* Date range */}
                    <TextField
                        type="date"
                        size="small"
                        label={t('page.consultation.filter.dateFrom', { defaultValue: 'Du' })}
                        value={dateFrom}
                        onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                    />
                    <TextField
                        type="date"
                        size="small"
                        label={t('page.consultation.filter.dateTo', { defaultValue: 'Au' })}
                        value={dateTo}
                        onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
                        InputLabelProps={{ shrink: true }}
                        sx={{ width: 160 }}
                    />

                    {/* Billing review filter */}
                    <Typography variant="body2" color="text.secondary">
                        {t('page.consultation.filter.billingReview.label', { defaultValue: 'Facturation :' })}
                    </Typography>
                    <ToggleButtonGroup
                        size="small"
                        value={billingReviewFilter}
                        exclusive
                        onChange={handleBillingReviewFilterChange}
                    >
                        <ToggleButton value="all">
                            {t('page.consultation.filter.billingReview.all', { defaultValue: 'Tous' })}
                        </ToggleButton>
                        <ToggleButton value={BillingReviewStatus.Pending}>
                            {t('page.consultation.filter.billingReview.pending', { defaultValue: 'En attente' })}
                        </ToggleButton>
                        <ToggleButton value={BillingReviewStatus.Issue} sx={{ color: 'error.main', '&.Mui-selected': { color: 'error.contrastText', bgcolor: 'error.main' } }}>
                            {t('page.consultation.filter.billingReview.issue', { defaultValue: 'Problèmes' })}
                        </ToggleButton>
                        <ToggleButton value={BillingReviewStatus.Completed}>
                            {t('page.consultation.filter.billingReview.completed', { defaultValue: 'Terminés' })}
                        </ToggleButton>
                    </ToggleButtonGroup>
                </Stack>

                {/* Summary bar */}
                <SummaryBar summary={summary} isLoading={isSummaryLoading} />

                {/* Table */}
                <WaitingRoomConsultationsTable
                    consultations={consultations}
                    isLoading={isLoading}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                    rowsPerPageOptions={limitOptions}
                    totalCount={total}
                    onValidateSelection={handleValidateSelection}
                    showPaymentInfo={showPaidOnly}
                    showDesmosStatus={hasDesmosConfigured}
                    selectedRows={selectedConsultationIds}
                    onSelectedRowsChange={setSelectedConsultationIds}
                />
            </Stack>
            <ConsultationValidationModal
                open={isValidationModalOpen}
                onClose={handleCloseValidationModal}
                onSubmit={handleValidationSubmit}
                selectedCount={selectedConsultationIds.length}
            />
            <ConsultationExportDialog
                open={isExportModalOpen}
                onClose={handleCloseExportModal}
                onExport={handleExport}
                isExporting={isExporting}
                networkMembers={networkMembers}
            />
        </Container>
    )
}
